import type { Asset, LogoStrategies, CoverStrategies } from '@seek/brand-types';
import type { UploadImageResponse } from '@seek/image-service-types';
import type { Locale } from '@seek/melways-sites';
import type { Badge, useToast } from 'braid-design-system';
import type { ChangeEvent, ComponentProps } from 'react';

import type {
  Brand,
  BrandOwnerType,
  PermissionOptions,
} from 'src/App/generated/graphql-types';

export enum Environment {
  DEV = 'development',
  STAGING = 'staging',
  PROD = 'production',
}

export enum AuthStatus {
  UNAUTHENTICATED = 'unauthenticated',
  AUTHENTICATED = 'authenticated',
  PENDING = 'pending',
}

export type OnNameChange = (event: ChangeEvent<HTMLInputElement>) => void;
export type OnFinish = (returnedBrand: Brand) => void;
export type NewBrand = Pick<Brand, 'name' | 'ownerId' | 'owner'>;

export type Callback = () => void;

export type NextAsset = UploadImageResponse;

export interface Editor {
  shouldRemoveCover?: boolean;
}

export interface BrandListItem extends Brand {
  keyType: BrandOwnerType;
  isLiveOnAd?: boolean;
}

export interface CoverCropperImage {
  croppedBoxData?: Cropper.CropBoxData;
  data?: Cropper.Data;
  dataUrl?: string;
}

export interface AssetWithCropData extends Asset<CoverStrategies> {
  file?: File;
  croppedCoverInfo: CoverCropperImage;
}

export type ExistingLogo = Asset<LogoStrategies>;
export interface NewLogo {
  file: File;
  url: string;
}
export type SelectedLogo = ExistingLogo | NewLogo;

export interface UserInfo {
  email: string | undefined;
  email_verified: boolean | undefined;
}

export interface AccountInfo {
  actAsSub: string | null;
  advertiserActivationPending: boolean;
  advertiserName: string;
  billingId: string;
  customerServiceView: boolean;
  email: string;
  hasMultipleAccounts: boolean;
  impersonatorUsername: string | null;
  isAgency: boolean;
  name: string;
  permissionOptions: PermissionOptions;
  showOnCreditHoldMessage: boolean;
  userId: number;
}

export type LayoutOverride = 'mobile' | 'desktop';

export type BadgeProps = ComponentProps<typeof Badge> & {
  children: 'Default' | 'On live ad';
};

type ShowToastFunction = ReturnType<typeof useToast>;
export type Toast = Parameters<ShowToastFunction>[0];

// Exclude 'zh-HK' for now as it's not supported in adv-header-footer
export type LocaleWithoutZhHK = Exclude<Locale, 'zh-HK'>;
